import { useState, useRef, useEffect } from "react";
import { MapPin, X } from "lucide-react";

export default function LocationAutocomplete({ value, onChange, onSelect, placeholder = "Search location…", required = false }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Sync value prop
  useEffect(() => { setQuery(value || ""); }, [value]);

  const search = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 3) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=6&countrycodes=in,ae,sa,qa,bh,om,kw`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await res.json();
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange && onChange(val);
    search(val);
  };

  const handleSelect = (item) => {
    // Build clean address
    const addr = item.address;
    const parts = [
      addr.amenity || addr.building || addr.shop,
      addr.road || addr.pedestrian,
      addr.suburb || addr.neighbourhood || addr.village,
      addr.city || addr.town || addr.county,
      addr.state,
      addr.country,
    ].filter(Boolean);
    const display = parts.join(", ");
    setQuery(display);
    setOpen(false);
    setSuggestions([]);
    onSelect && onSelect({
      display,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
    });
  };

  const clear = () => {
    setQuery("");
    setSuggestions([]);
    setOpen(false);
    onChange && onChange("");
    onSelect && onSelect({ display: "", lat: null, lng: null });
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative flex items-center">
        <MapPin className="absolute left-3 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          className="input-base"
          style={{ paddingLeft: "2.25rem", paddingRight: "2rem" }}
          placeholder={placeholder}
          value={query}
          onChange={handleInput}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          required={required}
          autoComplete="off"
        />
        {query && (
          <button type="button" onClick={clear} className="absolute right-3 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {loading && (
        <p className="text-xs text-muted-foreground mt-1 ml-1">Searching…</p>
      )}

      {open && suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden">
          {suggestions.map((item, i) => {
            const addr = item.address;
            const main = addr.amenity || addr.building || addr.road || addr.suburb || addr.city || addr.town || item.display_name.split(",")[0];
            const sub = [addr.city || addr.town || addr.county, addr.state, addr.country].filter(Boolean).join(", ");
            return (
              <button
                key={i}
                type="button"
                onClick={() => handleSelect(item)}
                className="w-full text-left px-4 py-3 hover:bg-brand-tint flex items-start gap-3 border-b border-border last:border-0 transition"
              >
                <MapPin className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground line-clamp-1">{main}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{sub}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
